import {attr, controller, target} from '@github/catalyst'
import {debounce} from '@github/mini-throttle'
import {announce} from '@github-ui/aria-live'
import {currencyFormatter, getUnitData} from './utils'
import type {UnitData} from './utils'

const wholeNumberPattern = /^\d+$/

function parseWholeNumber(value: string): number {
  const normalizedValue = value.trim()
  if (!normalizedValue || !wholeNumberPattern.test(normalizedValue)) return 0

  const count = Number(normalizedValue)
  return Number.isSafeInteger(count) ? count : 0
}

function isValidWholeNumber(value: string): boolean {
  const normalizedValue = value.trim()
  return (
    normalizedValue === '' ||
    (wholeNumberPattern.test(normalizedValue) && Number.isSafeInteger(Number(normalizedValue)))
  )
}

@controller('code-quality-calculator')
export class CodeQualityCalculatorElement extends HTMLElement {
  @target declare committerCountInput: HTMLInputElement
  @target declare aiCreditsInput: HTMLInputElement
  @target declare licenseCostDisplay: HTMLElement
  @target declare aiCreditCostDisplay: HTMLElement
  @target declare costPerMonthDisplay: HTMLElement

  @attr costPerMonth = 0
  @attr active = false

  declare unit_data: UnitData
  #debouncedAnnounceCost?: ReturnType<typeof debounce<[string]>>

  get committerCount() {
    return parseWholeNumber(this.committerCountInput.value)
  }

  get aiCredits() {
    return parseWholeNumber(this.aiCreditsInput.value)
  }

  get licenseCost() {
    return this.committerCount * (this.unit_data.code_quality_committer?.value || 0)
  }

  get aiCreditCost() {
    return this.aiCredits * (this.unit_data.code_quality_ai_credit?.value || 0)
  }

  connectedCallback() {
    this.unit_data = getUnitData()
    this.#debouncedAnnounceCost = debounce(
      (message: string) => announce(message, {element: this.costPerMonthDisplay}),
      1000,
    )
    this.updateCost()
  }

  disconnectedCallback() {
    this.#debouncedAnnounceCost?.cancel()
    this.#debouncedAnnounceCost = undefined
  }

  updateCosts(event: Event) {
    const input = event.target
    if (!(input instanceof HTMLInputElement)) return

    if (isValidWholeNumber(input.value)) {
      input.removeAttribute('aria-invalid')
    } else {
      input.setAttribute('aria-invalid', 'true')
    }

    this.updateCost()
    this.announceCost()
  }

  updateCost() {
    const licenseCost = this.licenseCost
    const aiCreditCost = this.aiCreditCost

    this.licenseCostDisplay.textContent = currencyFormatter.format(licenseCost / 100)
    this.aiCreditCostDisplay.textContent = currencyFormatter.format(aiCreditCost / 100)
    this.costPerMonth = licenseCost + aiCreditCost
    this.costPerMonthDisplay.textContent = currencyFormatter.format(this.costPerMonth / 100)

    this.dispatchState()
  }

  announceCost() {
    // eslint-disable-next-line i18n-text/no-en
    this.#debouncedAnnounceCost?.(`Total cost per month: ${currencyFormatter.format(this.costPerMonth / 100)} USD`)
  }

  dispatchState() {
    document.dispatchEvent(
      new CustomEvent('subtotalChanged', {
        detail: {
          sectionName: 'codeQualityTotal',
          amount: this.active ? this.costPerMonth : 0,
          active: this.active,
        },
      }),
    )

    document.dispatchEvent(
      new CustomEvent('formDataUpdated', {
        detail: {
          sectionName: 'codeQuality',
          resetEnabled: this.committerCountInput.value !== '' || this.aiCreditsInput.value !== '',
        },
      }),
    )
  }

  resetButtonClicked(event: Event) {
    event.preventDefault()
    this.committerCountInput.value = ''
    this.aiCreditsInput.value = ''
    this.committerCountInput.removeAttribute('aria-invalid')
    this.aiCreditsInput.removeAttribute('aria-invalid')
    this.updateCost()
    this.announceCost()
  }

  getSummaryForCSV() {
    return [
      [
        'GitHub Code Quality',
        `"Committers: ${this.committerCount}, AI Credits: ${this.aiCredits}"`,
        `"License Cost Per Month: ${currencyFormatter.format(
          this.licenseCost / 100,
        )}, AI Credit Cost Per Month: ${currencyFormatter.format(this.aiCreditCost / 100)}"`,
        `"${currencyFormatter.format(this.costPerMonth / 100)}"`,
      ],
    ]
  }

  static observedAttributes = ['data-active']
  attributeChangedCallback(name: string, oldValue: string | null, newValue: string | null): void {
    if (oldValue === newValue || name !== 'data-active') return

    this.active = newValue === 'true' || newValue === ''
    if (this.active) {
      this.updateCost()
    } else {
      document.dispatchEvent(
        new CustomEvent('subtotalChanged', {
          detail: {sectionName: 'codeQualityTotal', amount: 0, active: false},
        }),
      )
    }
  }
}
